import { Box, Text, Button, Heading, Divider } from '@wix/design-system';
import classNames from 'classnames';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { useWrongArticleBI } from './bi';
import { DATA_HOOKS } from '../../../dataHooks';
import useWrongArticleVersion from '../../../hooks/useWrongArticleVersion';

import css from './index.module.scss';

interface WrongArticleProps {
  description: string;
  onClickFeedback: () => void;
  articleId: string;
}

export const WrongArticle = ({
  description,
  onClickFeedback,
  articleId,
}: WrongArticleProps) => {
  const { t } = useTranslation();
  const version = useWrongArticleVersion();
  const { wrapperRef, onButtonClick } = useWrongArticleBI(articleId);

  const handleButtonClick = () => {
    const buttonText = t('article-page.wrong-article.button');
    void onButtonClick(buttonText);
    onClickFeedback();
  };

  if (!version) {
    return null;
  }

  const renderTitle = () => (
    <Heading size="medium">{t('article-page.wrong-article.title')}</Heading>
  );

  const renderDescription = () => (
    <Text size="small" weight="thin" className={css.wrongArticleDescription}>
      {description}
    </Text>
  );

  const renderButton = () => (
    <Button
      className={css.wrongArticleButton}
      onClick={handleButtonClick}
      skin="standard"
      dataHook={DATA_HOOKS.WRONG_ARTICLE_YES_BUTTON}
    >
      {t('article-page.wrong-article.button')}
    </Button>
  );

  const renderContent = () => {
    if (version === 'versionB') {
      return (
        <>
          <Box direction="vertical" gap="SP1">
            {renderDescription()}
            <Box
              direction="horizontal"
              align="space-between"
              verticalAlign="middle"
            >
              {renderTitle()}
              {renderButton()}
            </Box>
          </Box>
        </>
      );
    }

    if (version === 'versionC') {
      return (
        <Box direction="vertical">
          <Box direction="vertical" gap="SP1">
            <Text size="medium" className={css.wrongArticleSubtitle}>
              {t('article-page.wrong-article.versionC.subtitle')}
            </Text>
            {renderDescription()}
          </Box>
          <Divider className={css.wrongArticleDivider} />
          <Box
            direction="horizontal"
            align="space-between"
            verticalAlign="middle"
          >
            {renderTitle()}
            {renderButton()}
          </Box>
        </Box>
      );
    }

    // versionA
    return (
      <>
        {renderTitle()}
        <Box direction="horizontal" gap="SP3">
          {renderDescription()}
          {renderButton()}
        </Box>
      </>
    );
  };
  return (
    <Box
      ref={wrapperRef}
      className={classNames(css.wrongArticleWrapper, {
        [css.wrongArticleWrapperVersionB]: version === 'versionB',
      })}
      marginTop="SP4"
      borderRadius="SP2"
      direction={version === 'versionB' ? 'horizontal' : 'vertical'}
      gap="SP1"
      padding="SP3"
    >
      {renderContent()}
    </Box>
  );
};
